import React, { ReactNode, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { FiMenu, FiX } from '../../utils/icons';
import { motion, AnimatePresence } from 'framer-motion';

interface LayoutProps {
  children: ReactNode;
  hideFooter?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, hideFooter = false }) => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navExpanded, setNavExpanded] = useState(true);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleNavbar = () => {
    setNavExpanded(!navExpanded);
  };
  
  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);
  
  // Page transition variants
  const pageVariants = {
    initial: {
      opacity: 0,
      x: -20
    },
    in: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: 20
    }
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.5
  };
  
  return (
    <div className="min-h-screen flex flex-col overflow-hidden">
      {!isAuthPage && (
        <>
          {/* Only render navbar for desktop view */}
          <div className="hidden lg:block">
            <Navbar expanded={navExpanded} onToggleExpand={toggleNavbar} />
          </div>
          
          {/* Mobile menu button */}
          <div className="fixed top-4 right-4 z-50 lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-full bg-foreground border border-border shadow-md hover:shadow-glow transition-all"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <FiX size={24} className="text-primary" />
              ) : (
                <FiMenu size={24} />
              )}
            </button>
          </div>

          {/* Mobile menu overlay */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <div 
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
                onClick={toggleMobileMenu}
                aria-hidden="true"
                style={{
                  animation: 'fadeIn 0.2s ease-in-out'
                }}
              />
            )}
          </AnimatePresence>
          
          {/* Mobile sidebar */}
          <div 
            className={`fixed inset-y-0 left-0 z-40 w-[280px] bg-foreground border-r border-border lg:hidden overflow-hidden transform transition-transform duration-300 ease-in-out ${
              mobileMenuOpen ? 'translate-x-0 shadow-lg' : '-translate-x-full'
            }`}
          >
            <div className="p-4 overflow-y-auto h-full">
              <Navbar mobile />
            </div>
          </div>
        </>
      )}
      
      <main 
        className={`flex-grow overflow-hidden transition-all duration-300 ease-in-out ${
          !isAuthPage && navExpanded ? 'lg:pl-64' : !isAuthPage ? 'lg:pl-20' : ''
        }`}
      >
        {/* Use CSS for page transitions */}
        <div className="animate-fadeIn">
          {children}
        </div>
      </main>
      
      {!isAuthPage && !hideFooter && (
        <div className={`transition-all duration-300 ease-in-out ${
          navExpanded ? 'lg:ml-64' : 'lg:ml-20'
        }`}>
          <Footer />
        </div>
      )}
    </div>
  );
};

export default Layout; 